import { PUBLIC_PATH, SADMIN_PATH } from '@app/shared/constants/paths.constant';
import { Roles } from '@app/shared/constants/roles.constant';

interface MenuLike {
  link?: string | null;
  children?: MenuLike[];
}

interface UserLike {
  role?: { name?: string | null } | null;
}

/**
 * Walk the menu tree and return the first item that exposes a real
 * link. Groups (Restaurant, Configuración) can carry `menuLink: null`
 * and serve only as containers — falling back to a hardcoded route
 * like `/dashboard` for those users triggers the AuthGuard
 * permission denial loop.
 */
export const findFirstAccessibleLink = (
  menu: MenuLike[] | null | undefined
): string | null => {
  // Defensive against non-array inputs — persisted store hydration
  // race or a failed `/menu/user` response can land here as null,
  // an object, or a string. `for-of` throws "not iterable" otherwise.
  if (!Array.isArray(menu)) return null;
  for (const item of menu) {
    if (item.link) return item.link;
    if (item.children?.length) {
      const childLink = findFirstAccessibleLink(item.children);
      if (childLink) return childLink;
    }
  }
  return null;
};

/**
 * Resolve where the operator should land after a successful auth
 * (password / google / loginSelect). Single source of truth so the
 * login page and `GuestGuard` can't drift apart — they used to,
 * which is why Google login hardcoded `/dashboard` and broke for
 * any role that doesn't include dashboard access.
 *
 *   1. Super admin → /sadmin/businesses
 *   2. Else → first accessible link in the menu tree
 *   3. Fallback → /login (lets the user retry; AccessDeniedScreen
 *      can also point here without looping).
 */
export const resolveAuthRedirect = (
  user: UserLike | null | undefined,
  menu: MenuLike[] | null | undefined
): string => {
  if (user?.role?.name === Roles.SuperAdmin) {
    return SADMIN_PATH.BUSINESSES;
  }
  const first = findFirstAccessibleLink(menu);
  return first ?? PUBLIC_PATH.LOGIN;
};
